import apiClient from '../config/api';

const workPermitService = {
  /**
   * Get all work permits
   * @param {object} filters - Filter options (workOrderId, permitTypeId, status, startDate, endDate)
   * @returns {Promise} Array of work permits
   */
  getWorkPermits: async (filters = {}) => {
    const params = {};
    if (filters.workOrderId) params.workOrderId = filters.workOrderId;
    if (filters.permitTypeId) params.permitTypeId = filters.permitTypeId;
    if (filters.status) params.status = filters.status;
    if (filters.startDate) params.startDate = filters.startDate.toISOString();
    if (filters.endDate) params.endDate = filters.endDate.toISOString();
    
    const response = await apiClient.get('/workpermit', { params });
    return response.data;
  },

  /**
   * Get work permit by ID
   * @param {number} permitId - Work permit ID
   * @returns {Promise} Work permit details
   */
  getWorkPermitById: async (permitId) => {
    const response = await apiClient.get(`/workpermit/${permitId}`);
    return response.data;
  },

  /**
   * Create work permit
   * @param {object} permitData - Work permit data
   * @returns {Promise} Created work permit
   */
  createWorkPermit: async (permitData) => {
    const response = await apiClient.post('/workpermit', permitData);
    return response.data;
  },

  /**
   * Update work permit
   * @param {number} permitId - Work permit ID
   * @param {object} permitData - Updated work permit data
   * @returns {Promise} Updated work permit
   */
  updateWorkPermit: async (permitId, permitData) => {
    const response = await apiClient.post(`/workpermit/update/${permitId}`, permitData);
    return response.data;
  },

  /**
   * Update work permit status
   * @param {number} permitId - Work permit ID
   * @param {string} status - New status
   * @param {string} reason - Optional reason
   * @returns {Promise} Updated work permit
   */
  updatePermitStatus: async (permitId, status, reason = null) => {
    const response = await apiClient.post(`/workpermit/update/${permitId}/status`, { status, reason });
    return response.data;
  },

  /**
   * Extend work permit
   * @param {number} permitId - Work permit ID
   * @param {object} extensionData - Extension data (extendedValidTo, extensionReason)
   * @returns {Promise} Extension details
   */
  extendPermit: async (permitId, extensionData) => {
    const response = await apiClient.post(`/workpermit/${permitId}/extend`, extensionData);
    return response.data;
  },

  /**
   * Delete work permit
   * @param {number} permitId - Work permit ID
   * @returns {Promise} Deletion result
   */
  deleteWorkPermit: async (permitId) => {
    const response = await apiClient.post(`/workpermit/delete/${permitId}`);
    return response.data;
  },

  /**
   * Get active work permits
   * @returns {Promise} Array of active work permits
   */
  getActivePermits: async () => {
    const response = await apiClient.get('/workpermit/active');
    return response.data;
  },

  /**
   * Get expiring work permits
   * @param {number} hoursAhead - Hours ahead to check (default: 2)
   * @returns {Promise} Array of expiring work permits
   */
  getExpiringPermits: async (hoursAhead = 2) => {
    const response = await apiClient.get('/workpermit/expiring', { params: { hoursAhead } });
    return response.data;
  },

  /**
   * Get work permits by work order
   * @param {number} workOrderId - Work order ID
   * @returns {Promise} Array of work permits
   */
  getPermitsByWorkOrder: async (workOrderId) => {
    const response = await apiClient.get('/workpermit', { params: { workOrderId } });
    return response.data;
  },

  /**
   * Get permit types (master)
   * @returns {Promise} Array of permit types
   */
  getPermitTypes: async () => {
    const response = await apiClient.get('/workpermit/types');
    return response.data;
  },

  /**
   * Get safety check masters
   * @param {number} permitTypeId optional filter
   * @returns {Promise} Array of safety checks
   */
  getSafetyChecks: async (permitTypeId = null) => {
    const params = {};
    if (permitTypeId) params.permitTypeId = permitTypeId;
    const response = await apiClient.get('/workpermit/safety-checks', { params });
    return response.data;
  },
};

export default workPermitService;

